// src/app/vote/VoteForm.tsx
'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ApSubject } from '@/lib/content/ap-subjects'

type Props = {
  subjectsByCategory: Record<string, ApSubject[]>
}

export default function VoteForm({ subjectsByCategory }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [otherTest, setOtherTest] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const totalSelected = selected.size
  const canSubmit = useMemo(
    () => !submitting && (totalSelected > 0 || otherTest.trim().length > 0),
    [submitting, totalSelected, otherTest],
  )

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code)
      else next.add(code)
      return next
    })
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch('/api/vote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({
          email: email.trim() || null,
          apSubjects: Array.from(selected),
          otherTest: otherTest.trim() || null,
          source: 'vote-page',
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        error?: string
      }
      if (!res.ok || !data.ok) {
        setError(data.error || 'Something went wrong. Please try again.')
        setSubmitting(false)
        return
      }
      setSubmitted(true)
    } catch {
      setError('Network error. Please try again.')
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <Card className="border-border bg-card/60 p-8 text-center">
        <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-emerald-500/10 p-3 text-emerald-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M16.704 5.29a1 1 0 010 1.42l-8 8a1 1 0 01-1.42 0l-4-4a1 1 0 111.42-1.42L8 12.58l7.29-7.29a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-foreground">Thanks — your vote is in.</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          We read every vote. The most-requested subjects get built first.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Button asChild variant="outline" className="border-border text-foreground">
            <Link href="/">Back to home</Link>
          </Button>
          <Button
            onClick={() => {
              setSubmitted(false)
              setSelected(new Set())
              setOtherTest('')
              setEmail('')
            }}
            className="bg-brand"
          >
            Vote again
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {/* AP subjects */}
      <section className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Which AP subjects do you want us to build first?
          </h2>
          <p className="text-sm text-muted-foreground">
            Pick as many as you like. We&apos;ll ship the most-requested ones first.
          </p>
        </div>

        {Object.entries(subjectsByCategory).map(([category, subjects]) => (
          <div key={category} className="space-y-2">
            <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
              {category}
            </h3>
            <div className="grid gap-2 sm:grid-cols-2">
              {subjects.map((s) => {
                const id = `ap-${s.code}`
                const checked = selected.has(s.code)
                return (
                  <label
                    key={s.code}
                    htmlFor={id}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${
                      checked
                        ? 'border-brand/60 bg-brand/5'
                        : 'border-border bg-card/40 hover:border-border'
                    }`}
                  >
                    <input
                      id={id}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(s.code)}
                      className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-cyan-500"
                    />
                    <span className="text-sm text-foreground">{s.name}</span>
                  </label>
                )
              })}
            </div>
          </div>
        ))}
      </section>

      {/* Other test */}
      <section className="space-y-2">
        <Label htmlFor="other-test" className="text-foreground">
          Other high school test you want (optional)
        </Label>
        <p className="text-sm text-muted-foreground">
          E.g. IB exams, SAT Subject Tests, state tests — anything a US high schooler takes.
        </p>
        <textarea
          id="other-test"
          value={otherTest}
          onChange={(e) => setOtherTest(e.target.value)}
          maxLength={500}
          placeholder="Tell us what else we should build…"
          rows={3}
          className="w-full resize-y rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
        <div className="text-right text-xs text-muted-foreground">{otherTest.length}/500</div>
      </section>

      {/* Email (optional) */}
      <section className="space-y-2">
        <Label htmlFor="email" className="text-foreground">
          Email (optional)
        </Label>
        <p className="text-sm text-muted-foreground">
          We&apos;ll email you when your requested test goes live. No spam.
        </p>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@school.edu"
          className="border-border bg-card text-foreground placeholder:text-muted-foreground"
        />
      </section>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300"
        >
          {error}
        </div>
      )}

      <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground">
          {totalSelected > 0
            ? `${totalSelected} AP subject${totalSelected > 1 ? 's' : ''} selected`
            : 'No AP subjects selected'}
        </div>
        <Button
          type="submit"
          disabled={!canSubmit}
          className="bg-brand hover:bg-brand-hover disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit vote'}
        </Button>
      </div>
    </form>
  )
}
